import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';

import { selectors } from '../../../store/CurrentUser.model';
import ssPropTypes from '../../../modules/ssPropTypes';
import withStore from '../../withStore';

import { requireLogin } from '../../shared/login/SignUpLoginModal';
import { addQueryStringParams } from '../../../modules/url';

import './HeaderLinks.scss';
import { useResponsiveEffect } from '../../../hooks';
import withHook from '../../withHook';

/**
 * HeaderLinks - this component renders the static links that appear in the client header
 *
 * Usage:
 * ```jsx
 * <HeaderLinks />
 * ```
 *
 * When the client user is logged in, the only link displayed will be Help
 * When the user is anonymous this bar will include links for Setup My Business,
 * Sign Up, and Log In in addition to the Help link.
 *
 * This component is connected to the redux store, and does not need any props passed
 * explicitly.
 *
 */
class HeaderLinks extends Component {
  static propTypes = {
    is: PropTypes.object,

    /** {Boolean} True if the user is authenticated, otherwise false */
    isLoggedIn: PropTypes.bool.isRequired,
    /**
     * {String} The providerId of the current user, if any
     */
    providerId: ssPropTypes.numberLike,

    /**
     * {Function} will be used when the component wishes to redirect
     * the user somewhere
     */
    linkTo: PropTypes.func,
    /** Name of the current state */
    stateName: PropTypes.string,
    /** Object of utms */
    utmParams: PropTypes.object,
  };

  static defaultProps = {
    is: {},
    providerId: null,
    linkTo: () => { },
    stateName: '',
    utmParams: {},
  };

  /**
   * Opens the user login modal
   */
  promptUserForLogin = async () => {
    const { stateName, linkTo } = this.props;

    if (typeof stateName === 'string' && stateName.startsWith('provider_signup')) {
      linkTo('auth.login');
    } else {
      const { userData } = await requireLogin('login', 'header');
      if (userData && userData.provider_id) {
        linkTo('provider', { id: userData.provider_id });
      }
    }
  };

  /**
   * Opens the user signup modal
   */
  promptUserForSignup = async () => {
    const { stateName, linkTo } = this.props;

    if (typeof stateName === 'string' && stateName.startsWith('provider_signup')) {
      linkTo('provider_signup.signup_or_login');
    } else {
      const { userData } = requireLogin('signup', 'header');
      if (userData && userData.provider_id) {
        linkTo('provider', { id: userData.provider_id });
      }
    }
  };

  render() {
    const {
      is,
      isLoggedIn,
      providerId,
      stateName,
      utmParams,
    } = this.props;

    let showBusinessSetup = true;

    if (typeof stateName === 'string' && stateName.startsWith('provider_signup')) {
      showBusinessSetup = false;
    }

    const setupMyBusinessText = (is.desktop && !is.wide) ? 'Set Up My\nBusiness' : 'Set Up My Business';
    const setupMyBusinessStyles = (is.desktop && !is.wide) ? {
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
    } : {};

    return (
      <div className="header-links-component">
        <ul>
          {!isLoggedIn && (
            <Fragment>
              {showBusinessSetup && (
                <li className="emp">
                  <a
                    href={addQueryStringParams('/join', {
                      source: 'header_for_business',
                      ...utmParams,
                    })}
                    style={setupMyBusinessStyles}
                  >
                    {setupMyBusinessText}
                  </a>

                </li>
              )}
              <li>
                <button
                  type="button"
                  onClick={this.promptUserForSignup}
                >Sign Up
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={this.promptUserForLogin}
                >Log In
                </button>
              </li>
            </Fragment>
          )}
          {!providerId && (
            <li>
              <a href={addQueryStringParams('https://styleseat.freshdesk.com/support/home', utmParams)}>Help</a>
            </li>
          )}

        </ul>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    isLoggedIn: selectors.isLoggedIn(state),
    providerId: selectors.getLoggedInProviderId(state),
    stateName: state.route.name,
    utmParams: {
      utm_source: state.route.params.utm_source,
      utm_campaign: state.route.params.utm_campaign,
      utm_content: state.route.params.utm_content,
      utm_medium: state.route.params.utm_medium,
      utm_term: state.route.params.utm_term,
    },
  };
}

const mapDispatchToProps = dispatch => ({
  linkTo: (route, params, notify = true) => dispatch.route.go({
    route,
    params,
    notify,
  }),
});

export default withStore(connect(mapStateToProps, mapDispatchToProps)(
  withHook(HeaderLinks, 'is', useResponsiveEffect),
));
